/**
 * Japanese render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'ja')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const japaneseLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'ぼかし' },
    change: { primary: '変更' },
    click: { primary: 'クリック' },
    dblclick: { primary: 'dblclick' },
    focus: { primary: 'フォーカス' },
    input: { primary: '入力' },
    keydown: { primary: 'キーダウン' },
    keypress: { primary: 'キープレス' },
    keyup: { primary: 'キーアップ' },
    load: { primary: '読み込み' },
    mousedown: { primary: 'マウス押下' },
    mouseenter: { primary: 'mouseenter' },
    mouseleave: { primary: 'mouseleave' },
    mousemove: { primary: 'mousemove' },
    mouseout: { primary: 'マウスアウト' },
    mouseover: { primary: 'マウスオーバー' },
    mouseup: { primary: 'マウス解放' },
    reset: { primary: 'リセット' },
    resize: { primary: 'サイズ変更' },
    scroll: { primary: 'スクロール' },
    submit: { primary: '送信' },
    touchcancel: { primary: 'touchcancel' },
    touchend: { primary: 'touchend' },
    touchmove: { primary: 'touchmove' },
    touchstart: { primary: 'touchstart' },
    unload: { primary: 'unload' },
  },
  logical: {
    and: { primary: 'そして' },
    bind: { primary: 'バインド' },
    changes: { primary: '変わったら' },
    contains: { primary: '含む' },
    else: { primary: 'そうでなければ' },
    end: { primary: '終わり' },
    equals: { primary: '等しい' },
    exists: { primary: '存在する' },
    has: { primary: 'ある' },
    have: { primary: 'ある' },
    includes: { primary: '含める' },
    is: { primary: 'である' },
    live: { primary: 'ライブ' },
    matches: { primary: '一致する' },
    not: { primary: 'ではない' },
    or: { primary: 'または' },
    otherwise: { primary: 'そうでなければ' },
    then: { primary: 'それから' },
    when: { primary: '時' },
    where: { primary: 'どこ' },
  },
  temporal: {
    h: { primary: '時' },
    hour: { primary: '時間' },
    hours: { primary: '時間' },
    millisecond: { primary: 'ミリ秒' },
    milliseconds: { primary: 'ミリ秒' },
    min: { primary: '分' },
    minute: { primary: '分' },
    minutes: { primary: '分' },
    ms: { primary: 'ms' },
    s: { primary: '秒' },
    second: { primary: '秒' },
    seconds: { primary: '秒' },
    times: { primary: '回' },
  },
  values: {
    body: { primary: 'ボディ' },
    detail: { primary: '詳細' },
    document: { primary: 'ドキュメント' },
    element: { primary: '要素' },
    event: { primary: 'イベント' },
    false: { primary: '偽' },
    it: { primary: 'それ' },
    its: { primary: 'その' },
    me: { primary: '私' },
    my: { primary: '私の' },
    myself: { primary: '私自身' },
    null: { primary: 'null' },
    result: { primary: '結果' },
    target: { primary: '対象' },
    true: { primary: '真' },
    undefined: { primary: '未定義' },
    value: { primary: '値' },
    window: { primary: 'ウィンドウ' },
    you: { primary: 'あなた' },
    your: { primary: 'あなたの' },
    yourself: { primary: 'あなた自身' },
  },
  attributes: {
    attribute: { primary: '属性' },
    attributes: { primary: '属性群' },
    class: { primary: 'クラス' },
    classes: { primary: 'クラス群' },
    properties: { primary: 'プロパティ群' },
    property: { primary: 'プロパティ' },
    style: { primary: 'スタイル' },
    styles: { primary: 'スタイル群' },
  },
  expressions: {
    at: { primary: 'で' },
    characters: { primary: '文字' },
    children: { primary: '子' },
    closest: { primary: '最も近い' },
    empty: { primary: '空' },
    'ends with': { primary: 'で終わる' },
    exclusive: { primary: '除く' },
    first: { primary: '最初' },
    'ignoring case': { primary: '大文字小文字を無視' },
    inclusive: { primary: '含む' },
    'joined by': { primary: 'で結合' },
    last: { primary: '最後' },
    'mapped to': { primary: 'に変換' },
    next: { primary: '次' },
    no: { primary: 'ない' },
    parent: { primary: '親' },
    prev: { primary: '前' },
    previous: { primary: '前' },
    random: { primary: 'ランダム' },
    some: { primary: 'いくつか' },
    'sorted by': { primary: 'でソート' },
    'split by': { primary: 'で分割' },
    'starts with': { primary: 'で始まる' },
    within: { primary: '以内' },
  },
};

registerLexicon('ja', japaneseLexicon);
